import axios from 'axios';
import { isAxiosError } from 'axios';

import { getRuntimeConfig } from '../runtime';
import { VendiaRuntimeUser } from '../runtime';

export type TableResponse<T> = {
  data: T[];
  count: number;
};

export type ListParams = {
  page?: number;
  limit?: number;
  query?: string;
  orderBy?: string;
  // Extra query params passed through to the backend (e.g. { type: 'service' }).
  extra?: Record<string, string>;
};

export type SelectOption = {
  label: string;
  value: string | number;
};

const runtime = getRuntimeConfig();

const commonHeaders = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest'
};

export const adminApi = axios.create({
  baseURL: runtime.adminAppUrl,
  withCredentials: true,
  headers: {
    ...commonHeaders,
    ...(runtime.csrfToken ? { 'X-CSRF-TOKEN': runtime.csrfToken } : {})
  }
});

export const legacyAuthApi = axios.create({
  baseURL: '/',
  withCredentials: true,
  headers: commonHeaders
});

let sessionExpiredHandler: (() => void) | null = null;

export function setSessionExpiredHandler(handler: (() => void) | null) {
  sessionExpiredHandler = handler;
}

adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      sessionExpiredHandler?.();
    }
    // 419 = CSRF token mismatch (session rotated). Refresh the token once via
    // GET app/token and replay the request, like the legacy axios interceptor.
    const config = isAxiosError(error) ? (error.config as (typeof error.config & { _csrfRetried?: boolean }) | undefined) : undefined;
    if (isAxiosError(error) && error.response?.status === 419 && config && !config._csrfRetried) {
      config._csrfRetried = true;
      try {
        const tokenResponse = await adminApi.get('token');
        const token = tokenResponse.data?.token as string | undefined;
        if (token) {
          adminApi.defaults.headers.common['X-CSRF-TOKEN'] = token;
          config.headers.set('X-CSRF-TOKEN', token);
        }
        return adminApi.request(config);
      } catch {
        /* fall through to the original error */
      }
    }
    return Promise.reject(error);
  }
);

// Legacy controllers answer some failures with { lang_key } instead of a
// human message (e.g. email_failed, current_password_error). Translate them
// through the api.* i18n namespace when a translation exists.
let langKeyTranslator: ((key: string) => string | null) | null = null;
export function setLangKeyTranslator(fn: ((key: string) => string | null) | null) {
  langKeyTranslator = fn;
}

export function apiErrorMessage(error: unknown, fallback = 'Request failed') {
  if (!isAxiosError(error)) return error instanceof Error && error.message ? error.message : fallback;
  const data = error.response?.data as { message?: string; error?: string; lang_key?: string; errors?: Record<string, string[]> } | undefined;
  const translatedKey = data?.lang_key ? langKeyTranslator?.(data.lang_key) : null;
  if (translatedKey) return translatedKey;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (error.response?.status === 403) return langKeyTranslator?.('access_denied') ?? 'Access denied';
  const firstValidationError = data?.errors ? Object.values(data.errors).flat()[0] : null;
  return firstValidationError ?? error.message ?? fallback;
}

export function apiValidationErrors(error: unknown) {
  if (!isAxiosError(error)) return {};
  const data = error.response?.data as { errors?: Record<string, string[]> } | undefined;
  return data?.errors ?? {};
}

export async function fetchMe() {
  const response = await adminApi.get('me');
  return response.data;
}

export async function bootstrapLegacySession() {
  await legacyAuthApi.get('legacy/', {
    headers: { Accept: 'text/html,application/xhtml+xml' }
  });
}

export async function login(username: string, password: string, remember: boolean) {
  const response = await legacyAuthApi.post('login', { username, password, remember });
  return response.data;
}

export async function logout() {
  const response = await legacyAuthApi.get('logout');
  return response.data;
}

export async function fetchAuthenticatedUser(): Promise<VendiaRuntimeUser> {
  const response = await fetchMe();
  return response.user ?? response;
}

// The authenticated `/admin/app/me` endpoint returns the same 401 for a guest
// on both valid and unknown tenant hosts. Probe the tenant-aware backend root
// first; valid tenants return 200, while unknown tenant hosts redirect/fail.
export async function probeTenant() {
  const response = await fetch('/legacy/', {
    method: 'GET',
    credentials: 'include',
    redirect: 'manual',
    headers: { Accept: 'text/html,application/xhtml+xml' }
  });
  if (!response.ok || response.type === 'opaqueredirect') {
    throw new Error('Tenant/backend unavailable');
  }
}

export async function fetchTable<T>(resource: string, params: ListParams = {}) {
  const response = await adminApi.get<TableResponse<T>>(resource, {
    params: {
      limit: params.limit ?? 10,
      page: params.page ?? 1,
      orderBy: params.orderBy ?? 'created_at desc',
      ...(params.query ? { query: params.query } : {}),
      ...(params.extra ?? {})
    }
  });
  return response.data;
}

export async function fetchRecord<T>(resource: string, id: number | string) {
  const response = await adminApi.get<T>(`${resource}/${id}`);
  return response.data;
}

// True when the payload carries a File/Blob anywhere (attachments, photo, logo…).
export function hasFiles(value: unknown): boolean {
  if (typeof File !== 'undefined' && value instanceof Blob) return true;
  if (Array.isArray(value)) return value.some(hasFiles);
  if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasFiles);
  return false;
}

// Mirror of the legacy `objectToFormData` helper ($form in Vue): nested objects
// become bracketed keys, booleans become 1/0, null/undefined are skipped.
export function toFormData(payload: Record<string, unknown>, form = new FormData(), prefix = ''): FormData {
  Object.entries(payload).forEach(([key, value]) => {
    const name = prefix ? `${prefix}[${key}]` : key;
    if (value === undefined || value === null) return;
    if (typeof File !== 'undefined' && value instanceof Blob) {
      form.append(name, value);
    } else if (Array.isArray(value)) {
      if (!value.length) return;
      value.forEach((entry, index) => {
        if (typeof File !== 'undefined' && entry instanceof Blob) form.append(`${name}[]`, entry);
        else if (entry && typeof entry === 'object') toFormData(entry as Record<string, unknown>, form, `${name}[${index}]`);
        else form.append(`${name}[${index}]`, entry == null ? '' : String(entry));
      });
    } else if (value instanceof Date) {
      form.append(name, value.toISOString());
    } else if (typeof value === 'object') {
      toFormData(value as Record<string, unknown>, form, name);
    } else if (typeof value === 'boolean') {
      form.append(name, value ? '1' : '0');
    } else {
      form.append(name, String(value));
    }
  });
  return form;
}

export async function createRecord<T>(resource: string, payload: Record<string, unknown>) {
  const body = hasFiles(payload) ? toFormData(payload) : payload;
  const response = await adminApi.post<{ success: boolean; data: T; message?: string }>(resource, body);
  return response.data;
}

export async function updateRecord<T>(resource: string, id: number | string, payload: Record<string, unknown>) {
  // Files cannot travel in a PUT body; Laravel reads multipart only on POST, so
  // spoof the verb with _method=PUT exactly like the legacy Form mixin.
  if (hasFiles(payload)) {
    const response = await adminApi.post<{ success: boolean; data: T; message?: string }>(`${resource}/${id}`, toFormData({ ...payload, _method: 'PUT' }));
    return response.data;
  }
  const response = await adminApi.put<{ success: boolean; data: T; message?: string }>(`${resource}/${id}`, payload);
  return response.data;
}

// POST {resource}/delete/many { ids } → { success, count, failed }
export async function bulkDeleteRecords(resource: string, ids: Array<string | number>) {
  const response = await adminApi.post<{ success: boolean; count?: number; failed?: number }>(`${resource}/delete/many`, { ids });
  return response.data;
}

// POST {resource}/email/{id} → { success } (422 { lang_key: 'email_failed' } when the party has no email)
export async function emailRecord(resource: string, id: string | number) {
  const response = await adminApi.post<{ success: boolean }>(`${resource}/email/${id}`, {});
  return response.data;
}

// Root of the MPS module (`/admin`), i.e. adminAppUrl without the trailing `/app`.
// Downloads, PDFs and public views live there, outside the `/app` API group.
export function moduleRootUrl() {
  return runtime.adminAppUrl.replace(/\/app\/?$/, '');
}

export function downloadUrl(resource: string, id: string | number) {
  return `${moduleRootUrl()}/download/${resource}/${id}`;
}

export function openDownload(resource: string, id: string | number) {
  window.open(downloadUrl(resource, id), '_blank', 'noopener');
}

// Custom field definitions for a resource (GET {resource}/create returns the
// extra_attributes definitions: [{ slug, name, type, options, required }]).
export type CustomFieldDefinition = {
  slug: string;
  name: string;
  type: string;
  options?: string | null;
  required?: number | boolean;
  [key: string]: unknown;
};

const customFieldCache = new Map<string, Promise<CustomFieldDefinition[]>>();
export function fetchCustomFields(resource: string): Promise<CustomFieldDefinition[]> {
  if (!customFieldCache.has(resource)) {
    customFieldCache.set(
      resource,
      adminApi
        .get(`${resource}/create`)
        .then((response) => {
          const data = response.data;
          const list = Array.isArray(data) ? data : Array.isArray(data?.attributes) ? data.attributes : [];
          return list.filter((field: CustomFieldDefinition) => field && field.slug);
        })
        .catch(() => [])
    );
  }
  return customFieldCache.get(resource)!;
}

// DELETE attachments/{id} (MediaController@deleteAttachment)
export async function deleteAttachment(id: string | number) {
  const response = await adminApi.delete<{ success: boolean }>(`attachments/${id}`);
  return response.data;
}

export async function deleteRecord(resource: string, id: number | string) {
  const response = await adminApi.delete<{ success: boolean; message?: string }>(`${resource}/${id}`);
  return response.data;
}

export async function fetchSelectOptions(endpoint: string): Promise<SelectOption[]> {
  const response = await adminApi.get(endpoint);
  // Spatie's syncRoles() consumes role names, while the legacy search endpoint
  // exposes roles as { value: uuid, label: name }. Keep UUIDs for display-only
  // consumers, but make the user form submit the contract value: the name.
  if (endpoint === 'roles/search') {
    const roles = Array.isArray(response.data) ? response.data : [];
    return roles.flatMap((role) => {
      if (!role || typeof role !== 'object') return [];
      const record = role as Record<string, unknown>;
      const name = record.name ?? record.label;
      return name ? [{ label: String(name), value: String(name) }] : [];
    });
  }
  return flattenOptions(response.data);
}

function flattenOptions(value: unknown): SelectOption[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const label = String(record.label ?? record.name ?? record.title ?? record.code ?? record.value ?? record.id ?? '');
    const option: SelectOption | null =
      record.value != null || record.id != null ? { label, value: (record.value ?? record.id) as string | number } : null;
    const children = flattenOptions(record.children);
    return option ? [option, ...children] : children;
  });
}

export async function fetchDashboard() {
  const now = new Date();
  const response = await adminApi.get('dashboard', {
    params: { month: now.getMonth() + 1, year: now.getFullYear() }
  });
  return response.data;
}

export async function fetchBusinessInsights() {
  const response = await adminApi.get('dashboard/insights');
  return response.data as { snapshot?: Record<string, unknown>; messages?: Array<Record<string, unknown>> };
}

export async function fetchReferenceTable<T>(resource: 'brands' | 'units' | 'categories' | 'taxes' | 'halls' | 'fields') {
  const response = await adminApi.get<TableResponse<T>>(resource, {
    params: { limit: 10, page: 1, orderBy: 'created_at desc' }
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// Application Settings (singleton key-value store, role:super only)
// ---------------------------------------------------------------------------
// GET settings returns { accounts, customers, categories, countries, timezones,
//   mail_settings, payment_settings, settings: { ...all mps_settings rows } }
// POST settings saves the flattened settings object (loyalty/auto_update_time
//   re-encoded to JSON, quick_cash joined with |).
// PUT settings writes mail/payment env values to .env (separate from main form).
export async function fetchSettings() {
  const response = await adminApi.get('settings');
  return response.data;
}

export async function saveSettings(payload: Record<string, unknown>) {
  const response = await adminApi.post('settings', payload);
  return response.data;
}

export async function saveEnvSettings(payload: Record<string, unknown>) {
  const response = await adminApi.put('settings', payload);
  return response.data;
}

// Item Label Design (singleton, role:super only)
export async function fetchLabelDesign() {
  const response = await adminApi.get('settings/label');
  return response.data;
}

export async function saveLabelDesign(payload: Record<string, unknown>) {
  const response = await adminApi.post('settings/label', payload);
  return response.data;
}

// Whether the tenant has loyalty configured (mps_settings.loyalty). The legacy
// sale-reversal path has a known bug that fires only when loyalty is enabled,
// so sale finalization is hard-gated on this returning false.
let cachedLoyaltyEnabled: boolean | null = null;
export async function isLoyaltyEnabled(): Promise<boolean> {
  if (cachedLoyaltyEnabled !== null) return cachedLoyaltyEnabled;
  try {
    const response = await adminApi.get('settings');
    const data = response.data;
    // The index() response nests loyalty under the settings key. It arrives
    // already JSON-decoded (object) from mps_config(null, true).
    const loyaltyRaw = (data?.settings?.loyalty ?? data?.loyalty) as string | object | null | undefined;
    const loyalty = typeof loyaltyRaw === 'string' ? safeJsonParse(loyaltyRaw) : loyaltyRaw;
    cachedLoyaltyEnabled = Boolean(loyalty && (loyalty.customer || loyalty.staff) && (loyalty.customer?.points || loyalty.staff?.points));
  } catch {
    // If we can't read settings, fail safe (treat as enabled → hide finalize).
    cachedLoyaltyEnabled = true;
  }
  return cachedLoyaltyEnabled;
}

function safeJsonParse(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
